import { conditionLabel } from '@skop-app/lib-domain/constants'
import type { Dimensions, Place } from '@skop-app/lib-domain/types'

import { Changelog } from '@/types/changelog'
import { getPlaceById } from '@/services/queries'
import {
  decodeChangeLogValue,
  isSystemChangeLogActor,
  parseChangeLogProperties,
  stringifyDecodedChangeLogValue,
} from '@/services/changelogValue'

/**
 * Parses the dimensions
 * @param dimension - The dimension to parse
 * @returns The parsed dimensions
 */
const isDimensionRecord = (dimension: unknown): dimension is Dimensions =>
  Boolean(dimension) &&
  typeof dimension === 'object' &&
  !Array.isArray(dimension)

const normalizeDimensionValue = (
  value: unknown,
): string | number | undefined => {
  if (value == null || value === '') return undefined
  if (typeof value === 'string' || typeof value === 'number') return value
  return String(value)
}

const parseDimensions = (dimension: unknown): Dimensions => {
  if (isDimensionRecord(dimension)) {
    return Object.entries(dimension).reduce<Dimensions>((acc, [key, value]) => {
      const normalized = normalizeDimensionValue(value)
      if (normalized !== undefined) {
        acc[key] = normalized
      }

      return acc
    }, {})
  }

  if (typeof dimension !== 'string') return {}

  try {
    const parsed = JSON.parse(dimension) as unknown
    return parseDimensions(parsed)
  } catch {
    // Keep support for legacy strings such as "{length=1,unit=cm}".
  }

  const match = /\{(.*?)\}/.exec(dimension)?.[1]
  if (!match) return {}

  return match.split(',').reduce((acc, curr) => {
    const [key, ...valueParts] = curr.trim().split('=')
    const value = valueParts.join('=')
    const normalized = normalizeDimensionValue(value)

    if (!key || normalized === undefined) return acc

    return { ...acc, [key]: normalized }
  }, {} as Dimensions)
}

/**
 * Compares the dimensions
 * @param oldDimension - The old dimension
 * @param newDimension - The new dimension
 * @returns The dimensions
 */
const compareDimensions = (
  oldDimension: unknown,
  newDimension: unknown,
): Dimensions[] => {
  const oldValue = parseDimensions(oldDimension)
  const newValue = parseDimensions(newDimension)
  const hasOldValue = Object.keys(oldValue).length > 0
  const hasNewValue = Object.keys(newValue).length > 0

  if (!hasOldValue && !hasNewValue) return []
  if (!hasOldValue) return [newValue]
  if (!hasNewValue) return [oldValue]

  const result: Dimensions = {}
  for (const key in newValue) {
    if (newValue[key] !== oldValue[key]) {
      result[key] = newValue[key]
    }
  }

  if (oldValue.unit || newValue.unit) {
    result.unit = newValue.unit || oldValue.unit
  }

  return [result]
}

/**
 * Extracts the number and unit from the input
 * @param input - The input to extract the number and unit from
 * @returns The number and unit from the input
 */
const extractNumberAndUnit = (
  input: string,
): { number: number; unit?: string } | null => {
  // First try to parse complex format with curly braces: {number=123,unit=Unité}
  const complexMatch = /\{number=(\d+)(?:,\s*unit=([^}]+))?\}/.exec(input)
  if (complexMatch) {
    const number = parseInt(complexMatch[1], 10)
    const unit = complexMatch[2]

    return {
      number,
      ...(unit && {
        unit:
          unit === 'Unité' && number > 1 ? 'unités' : unit.toLocaleLowerCase(),
      }),
    }
  }

  // If complex format doesn't match, try simple numeric string
  const simpleMatch = /^(\d+)$/.exec(input?.trim())
  if (simpleMatch) {
    const number = parseInt(simpleMatch[1], 10)
    return { number }
  }

  return null
}

/**
 * Formats the place
 * @param place - The place to format
 * @param t - The translation function
 * @returns The formatted place
 */
const formatPlace = (place: Place, t: (key: string) => string) => {
  return `${place?.name ? `${place?.name} ${place?.type ? `(${t(`PlaceTypes.${place?.type}`)})` : ''}, ` : ''}${place?.address}${`${place?.address && place?.zipcode ? ',' : ''}${place?.zipcode} ${place?.city}`}`
}

/**
 * Extracts the properties from the changelog
 * @param oldValue - The old changelog value to extract the properties from
 * @param newValue - The new changelog value to extract the properties from
 * @param keysToSearch - The keys to search for in the changelog (optional)
 * @returns The properties from the changelog
 */
const extractProperties = (
  oldValue: unknown,
  newValue: unknown,
  keysToSearch: string[] = [],
) => {
  const oldProperties = parseChangeLogProperties(oldValue)
  const newProperties = parseChangeLogProperties(newValue)

  const changes: { key: string; oldValue: unknown; newValue: unknown }[] = []

  const oldMap = new Map(oldProperties.map((prop) => [prop.key, prop]))
  const newMap = new Map(newProperties.map((prop) => [prop.key, prop]))

  const allKeys =
    keysToSearch.length > 0
      ? keysToSearch
      : Array.from(
          new Set([...Array.from(oldMap.keys()), ...Array.from(newMap.keys())]),
        )

  allKeys.forEach((key) => {
    const oldProp = oldMap.get(key)
    const newProp = newMap.get(key)

    if (!oldProp && !newProp) return

    if (!oldProp && newProp) {
      changes.push({
        key,
        oldValue: null,
        newValue: newProp.values[0],
      })
    } else if (oldProp && !newProp) {
      changes.push({
        key,
        oldValue: oldProp.values[0],
        newValue: null,
      })
    } else if (
      oldProp &&
      newProp &&
      JSON.stringify(oldProp.values) !== JSON.stringify(newProp.values)
    ) {
      changes.push({
        key,
        oldValue: oldProp.values[0],
        newValue: newProp.values[0],
      })
    }
  })

  return changes
}

const formatPropertyChangeValue = (key: string, value: unknown): string => {
  if (value == null) return ''

  if (key === 'startDate' || key === 'endDate') {
    return new Date(stringifyDecodedChangeLogValue(value)).toLocaleDateString()
  }

  return stringifyDecodedChangeLogValue(value)
}

export const changeLogsAdapter = async (
  changelogs: Changelog[],
  t: (
    key: string,
    params?: Record<string, string | number | undefined | null>,
  ) => string,
) => {
  const processedChangelogs = await Promise.all(
    changelogs?.map(async (changelog) => {
      const rawOldValue = decodeChangeLogValue(changelog.oldValue)
      const rawNewValue = decodeChangeLogValue(changelog.newValue)
      const oldValue = stringifyDecodedChangeLogValue(rawOldValue)
      const newValue = stringifyDecodedChangeLogValue(rawNewValue)
      const changedBy = isSystemChangeLogActor(changelog.changedBy)
        ? t('Changelogs.system-user')
        : changelog?.user?.email === 'unknown'
          ? t('Changelogs.unknown-user')
          : changelog?.user?.name

      switch (changelog.field) {
        case 'category':
          return {
            id: changelog.id,
            changedBy,
            description:
              oldValue === 'NOT_EXIST'
                ? t('Changelogs.add-category', { category: newValue })
                : t('Changelogs.modify-category', {
                    oldCategory: oldValue,
                    newCategory: newValue,
                  }),
            date: changelog.changedAt,
            icon: oldValue === 'NOT_EXIST' ? 'Plus' : 'TagSimple',
          }
        case 'quantity.number': {
          const oldQuantityData = extractNumberAndUnit(oldValue)
          const newQuantityData = extractNumberAndUnit(newValue)

          const oldQuantity = oldQuantityData?.number ?? 0
          const newQuantity = newQuantityData?.number ?? 0
          const unit = newQuantityData?.unit || oldQuantityData?.unit || ''

          let description: string
          let icon: string

          if (oldValue === 'NOT_EXIST') {
            // Initial quantity set
            description = t('Changelogs.add-quantity-set', {
              quantity: newQuantity,
              unit: unit,
            })
            icon = 'Plus'
          } else {
            const quantityDifference = newQuantity - oldQuantity

            if (quantityDifference > 0) {
              // Quantity increased
              description = t('Changelogs.add-quantity-increased', {
                quantity: quantityDifference,
                unit: quantityDifference,
              })
              icon = 'Plus'
            } else if (quantityDifference < 0) {
              // Quantity decreased
              description = t('Changelogs.add-quantity-decreased', {
                quantity: Math.abs(quantityDifference),
                unit: Math.abs(quantityDifference),
              })
              icon = 'Minus'
            } else {
              // Fallback to old format if no change (shouldn't happen)
              description = t('Changelogs.modify-quantity', {
                oldQuantity,
                newQuantity,
                oldUnit: oldQuantityData?.unit || '',
                newUnit: newQuantityData?.unit || '',
              })
              icon = 'PlusMinus'
            }
          }

          return {
            id: changelog.id,
            changedBy,
            description,
            date: changelog.changedAt,
            icon,
          }
        }
        case 'action':
          return {
            id: changelog.id,
            changedBy,
            description: t('Changelogs.add-action', {
              action: t(`Label.${newValue}`),
            }),
            date: changelog.changedAt,
            icon: 'Path',
          }
        case 'dimension': {
          const dimensions = compareDimensions(rawOldValue, rawNewValue)

          const changes = dimensions.map((change) => {
            const hasOtherDimensions = Object.keys(change).some(
              (key) => key !== 'unit',
            )
            return Object.entries(change)
              .map(([key, value]) => {
                if (key === 'unit' && !hasOtherDimensions) {
                  return t('Changelogs.modify-dimension-unit', {
                    unit: value,
                  })
                }

                if (key === 'unit' && hasOtherDimensions) {
                  return null
                }

                return t('Changelogs.modify-dimension', {
                  key: t(`Product.${key}`),
                  unit: change.unit || '',
                  value: value,
                })
              })
              .filter(Boolean)
          })

          return {
            id: changelog.id,
            changedBy,
            description: changes.flat().filter(Boolean).join(', '),
            date: changelog.changedAt,
            icon: 'Ruler',
          }
        }
        case 'placeID':
          try {
            const place = await getPlaceById(newValue)
            return {
              id: changelog.id,
              changedBy,
              description: t('Changelogs.modify-place', {
                place: formatPlace(place, t),
              }),
              date: changelog.changedAt,
              icon: 'MapPin',
            }
          } catch (error) {
            console.error('Failed to fetch place details for changelog:', error)
            return {
              id: changelog.id,
              changedBy,
              description: t('Changelogs.modify-place-error', {
                placeId: newValue,
              }),
              date: changelog.changedAt,
              icon: 'MapPinWarning', // Or a generic error icon
            }
          }
        case 'condition':
          return {
            id: changelog.id,
            changedBy,
            description:
              oldValue === 'NOT_EXIST'
                ? t('Changelogs.add-condition', {
                    condition: t(
                      `ConditionLabel.${
                        conditionLabel.find(
                          (condition) => condition.label === newValue,
                        )?.value
                      }`,
                    ),
                  })
                : t('Changelogs.modify-condition', {
                    oldCondition: t(
                      `ConditionLabel.${
                        conditionLabel.find(
                          (condition) => condition.label === oldValue,
                        )?.value
                      }`,
                    ),
                    newCondition: t(
                      `ConditionLabel.${
                        conditionLabel.find(
                          (condition) => condition.label === newValue,
                        )?.value
                      }`,
                    ),
                  }),
            date: changelog.changedAt,
            icon: 'Sparkle',
          }
        case 'brand':
          return {
            id: changelog.id,
            changedBy,
            description:
              oldValue === 'NOT_EXIST'
                ? t('Changelogs.add-brand', {
                    brand: newValue,
                  })
                : t('Changelogs.modify-brand', {
                    oldBrand: oldValue,
                    newBrand: newValue,
                  }),
            date: changelog.changedAt,
            icon: 'TagSimple',
          }
        case 'properties': {
          const properties = extractProperties(rawOldValue, rawNewValue, [
            'startDate',
            'endDate',
            'price',
            'ref',
          ])

          const title = properties.map((change, index) => {
            const newValue = formatPropertyChangeValue(
              change.key,
              change.newValue,
            )
            const oldValue = formatPropertyChangeValue(
              change.key,
              change.oldValue,
            )

            if (change.oldValue === null) {
              return `${index > 0 ? ', ' : ''}${t('Changelogs.add-property', {
                key: t(`Changelogs.properties.${change.key}`),
                value: newValue,
              })}`
            }
            return `${index > 0 ? ', ' : ''}${t('Changelogs.modify-property', {
              key: t(`Changelogs.properties.${change.key}`),
              oldValue,
              newValue,
            })}`
          })

          if (title.length > 0) {
            return {
              id: changelog.id,
              changedBy,
              description: title.join(''),
              date: changelog.changedAt,
              icon: 'Rows',
            }
          } else {
            return null
          }
        }
        default:
          return null
      }
    }),
  )

  return processedChangelogs.filter(Boolean)
}
